package com.example.mystybag.core.extensions

import androidx.compose.ui.text.AnnotatedString
import androidx.compose.ui.text.SpanStyle
import androidx.compose.ui.text.buildAnnotatedString
import androidx.compose.ui.text.font.FontStyle
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextDecoration
import androidx.compose.ui.text.withStyle
import com.example.mystybag.core.defines.UtilsTransfersDefines
import kotlin.math.round
import kotlin.math.roundToInt

fun String.equalsIgnoreCase(value: String?): Boolean = equals(value, ignoreCase = true)

fun String?.capitalizeFirst(): String {
    return this?.replaceFirstChar { it.uppercase() } ?: ""
}

fun isWhitespace(char: Char): Boolean {
    val codePoint: Int = char.code
    // We don't just call into icu4c because of the JNI overhead. Ideally we'd fix that.
    // Any ASCII whitespace character?
    if (codePoint in 0x1c..0x20 || codePoint in 0x09..0x0d) {
        return true
    }
    if (codePoint < 0x1000) {
        return false
    }
    // OGHAM SPACE MARK or MONGOLIAN VOWEL SEPARATOR?
    if (codePoint == 0x1680 || codePoint == 0x180e) {
        return true
    }
    if (codePoint < 0x2000) {
        return false
    }
    // Exclude General Punctuation's non-breaking spaces (which includes FIGURE SPACE).
    if (codePoint == 0x2007 || codePoint == 0x202f) {
        return false
    }
    return if (codePoint <= 0xffff) {
        // Other whitespace from General Punctuation...
        codePoint <= 0x200a || codePoint == 0x2028 || codePoint == 0x2029 || codePoint == 0x205f || codePoint == 0x3000 // ...or CJK Symbols and Punctuation?
    }
    else
        false
}

fun generateRandomString(length: Int = 32): String {
    val pool = "AaBbCcDdEeFfGgHhIiJjKkLlMmNnOoPpQqRrSsTtUuVvWwXxYyZz0123456789"
    return List(length) { pool.random() }.joinToString("")
}

val String?.normalizedPhoneNumber: String
    get() {
        return if (this == null)
            ""
        else if (this.trim().startsWith("00"))
            this.replaceFirst("00", "+").replace(" ", "")
        else if (this.trim().startsWith("0")) {
            if ("getLoginPhoneDialCode()".isNotEmpty())
                "getLoginPhoneDialCode()" + this.trim().substring(1).replace(" ", "")
            else
                this.trim().replace(" ", "")
        }
        else
            this.trim().replace(" ", "")
    }

fun String?.ifNullOrEmpty(block: () -> String): String = this?.ifEmpty(block) ?: block()


fun String.addDownloadLink() : String {
    return if (this.trim().isEmpty()) "" else "downloadImageLink".plus(this)
}

fun String.removeTrailingText(textToRemove: String): String {
    return this.indexOf(textToRemove, ignoreCase = true).takeIf { it > -1 }?.let {
        this.take(it)
    } ?: this
}

inline fun <T> String.ifNotEmpty(block: (String) -> T?): T? {
    return this.takeIf { it.isNotEmpty() }?.let(block)
}

fun String.formatAmount(): String {
    try {
        if (this.equalsIgnoreCase("-1") || this.equalsIgnoreCase("N/A") || this.equalsIgnoreCase("+N/A"))
            return this

        if (!this.equalsIgnoreCase("")) {
            val formattedAmount = (this.replace(",", ".").replace("+", "").replace("-", "").toDouble() * 100.00).roundToInt().toDouble() / 100.00

            var amountString = formattedAmount.toString().replace(',', '.')
            if (amountString.contains(".") && amountString.substring(amountString.indexOf(".")).length == 2) {
                amountString += "0"
            } else if (!amountString.contains("."))
                amountString += ".00"

            return when {
                this.startsWith("+") -> "+$amountString"
                this.startsWith("-") -> "-$amountString"
                else                 -> amountString
            }
        }
    } catch (e: Exception) {
        e.printStackTrace()
    }
    return ""
}

fun String.formatPan(): String {
    var pan = this

    try {
        pan = this.substring(0, 4) + " " + this.substring(4, 8) + " " + this.substring(8, 12) + " " + this.substring(12)
    } catch (e: Exception){
        e.printStackTrace()
    }

    return pan
}

fun String?.separate(other: String?): String {
    return other?.let { this?.plus(" ").plus(other) } ?: this ?: ""
}

val String?.isValidEmail : Boolean
    get() = this?.matches(Regex(UtilsTransfersDefines.EMAIL_PATTERN)) == true


val String?.isValidPhone : Boolean
    get() = this?.matches(Regex(UtilsTransfersDefines.PHONE_PATTERN)) == true

val String?.isPhoneValueChangedValid : Boolean
    get() = this?.matches(Regex("[+\\d ]+")) == true

fun String.getExpireDate(): String = if (this.length == 4) this.substring(2, 4) + "/" + this.substring(0, 2) else ""

fun String.addColons() : String = "$this:"

fun String.addLineBreakIf(condition: Boolean = true): String = if (condition) "$this\n" else this
fun String.addLineBreak(count: Int = 1): String = this.plus("\n".repeat(count))

fun String?.convertBgnToEur() =
    this?.let {
        (round((this.toDouble() / 1.95583) * 100.0) / 100.0).toString().formatAmount()
    } ?: ""

fun String.formatStringWithSpaces(): String = this.replace(Regex("(\\d)(?=(\\d{3})+(?!\\d))"), "$1 ")

val String.asImageLink: String
get() = "baseImageLink".plus(this.lowercase())

val String.asCountryImageLink: String
    get() = this.lowercase().let { "countryImageLink".replace("%%", it) }

fun String.setStartUnicode(unicode: String): String = "$unicode $this"

fun String.zeroPrefix() = if (this.length == 1) "0$this" else this

fun String.addCharAtIndex(char: Char, index: Int) = StringBuilder(this).apply { insert(index, char) }.toString()

fun String.formatNames(): String = this.split(" ").joinToString(" ") { part ->
    part.split("-").joinToString("-") { word ->
        word.lowercase().replaceFirstChar { char ->
            char.uppercase()
        }
    }
}

fun String.fromHtml(): AnnotatedString {

    fun getStyle(tags: List<String>): SpanStyle {
        var style = SpanStyle()

        if ("b" in tags)
            style = style.merge(SpanStyle(fontWeight = FontWeight.Bold))

        if ("i" in tags)
            style = style.merge(SpanStyle(fontStyle = FontStyle.Italic))

        if ("u" in tags)
            style = style.merge(SpanStyle(textDecoration = TextDecoration.Underline))

        return style
    }

    val tagRegex = Regex("\\[(/?)([biu])\\]", RegexOption.IGNORE_CASE)
    return buildAnnotatedString {
        val openTags = mutableListOf<String>()
        var currentIndex = 0

        tagRegex.findAll(this@fromHtml).forEach { match ->
            val tagStart = match.range.first
            val tagEnd = match.range.last + 1
            val isClosing = match.groupValues[1] == "/"
            val tagName = match.groupValues[2].lowercase()

            if (tagStart > currentIndex) {
                val text = this@fromHtml.substring(currentIndex, tagStart)
                withStyle(getStyle(openTags)) {
                    append(text)
                }
            }

            if (isClosing) {
                val lastIndex = openTags.lastIndexOf(tagName)
                if (lastIndex != -1)
                    openTags.removeAt(lastIndex)
            }
            else
                openTags.add(tagName)

            currentIndex = tagEnd
        }

        if (currentIndex < this@fromHtml.length) {
            withStyle(getStyle(openTags)) {
                append(this@fromHtml.substring(currentIndex))
            }
        }
    }
}